import {isAdminView,viewToken,clearView,waitForAdminView} from './admin-view.js';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { loginDestination } from './owner-navigation.js';
import { connectPreviewLayout } from './preview-layout.js';

import Login from './pages/Login.jsx';
import AuthVerify from './pages/AuthVerify.jsx';
import Home from './pages/Home.jsx';
import Settings from './pages/Settings.jsx';
import Business from './pages/Employee.jsx';
import Billing from './pages/Billing.jsx';
import Growth from './pages/Growth.jsx';
import TabBar from './components/TabBar.jsx';
import InstallPrompt from './components/InstallPrompt.jsx';
import { IconLogo } from './components/Icons.jsx';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

// --- TOKEN HELPERS ------------------------------------------------------------
// Store in BOTH localStorage and cookie for iOS PWA persistence.
// iOS Safari in standalone mode can clear localStorage between launches.
// A first-party cookie (30-day, SameSite=Lax) survives this.

function setToken(jwt, gymId) {
  try { localStorage.setItem('aigo_token', jwt); } catch {}
  if (gymId) try { localStorage.setItem('aigo_gym_id', gymId); } catch {}
  // Set cookie as backup (30 days)
  const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `aigo_token=${jwt}; path=/; expires=${expires}; SameSite=Lax; Secure`;
}

function getToken() {
  if(isAdminView)return viewToken();
  // Try localStorage first, then cookie
  const ls = localStorage.getItem('aigo_token');
  if (ls) return ls;
  const match = document.cookie.match(/(?:^|;\s*)aigo_token=([^;]+)/);
  const ck = match ? match[1] : null;
  // If found in cookie but not localStorage, restore localStorage
  if (ck) { try { localStorage.setItem('aigo_token', ck); } catch {} }
  return ck;
}

function clearToken() {
  if(isAdminView){clearView();return;}
  localStorage.removeItem('aigo_token');
  localStorage.removeItem('aigo_gym_id');
  document.cookie = 'aigo_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure';
}

function authFetch(endpoint, opts = {}) {
  if(isAdminView && opts.method && opts.method!=='GET')return Promise.resolve(new Response(JSON.stringify({error:'Admin owner view is read-only. Use the admin tabs to make changes.'}),{status:403,headers:{'Content-Type':'application/json'}}));
  const token = getToken();
  return fetch(`${API_BASE}${endpoint}`, {
    ...opts,
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

// --- GYM BRANDING CONTEXT -----------------------------------------------------

const BrandingContext = createContext({
  gymName: 'AiGO',
  logoUrl: null,
  primaryColor: '#6366f1',
});

export function useBranding() {
  return useContext(BrandingContext);
}

function GymBrandingProvider({ children }) {
  const [branding, setBranding] = useState({ gymName: 'AiGO', logoUrl: null, primaryColor: '#6366f1' });

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--primary', branding.primaryColor);
    const meta = document.getElementById('theme-color-meta');
    if (meta) meta.setAttribute('content', branding.primaryColor);
  }, [branding.primaryColor]);

  useEffect(() => {
    authFetch('/dashboard')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.gym) return;
        const fresh = {
          gymName: data.gym.name || 'AiGO',
          logoUrl: data.gym.logo_url || null,
          primaryColor: data.gym.primary_color || '#6366f1',
        };
        setBranding(fresh);

      })
      .catch(() => {});
  }, []);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}

// --- JWT FROM URL HANDLER -----------------------------------------------------
// After magic link verify, the API redirects to /?jwt=xxx
// This component reads it, stores it, and strips it from the URL.

// Read JWT from URL SYNCHRONOUSLY before React renders.
// This must happen before AuthGuard checks for a token.
(function handleJwtFromUrl() {
  if(isAdminView)return;
  const params = new URLSearchParams(window.location.search);
  const jwt = params.get('jwt');
  if (jwt) {
    // Store immediately (synchronous)
    try { localStorage.setItem('aigo_token', jwt); } catch {}
    const expires = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `aigo_token=${jwt}; path=/; expires=${expires}; SameSite=Lax; Secure`;
    // Strip jwt from URL
    window.history.replaceState({}, '', window.location.pathname);
  }
})();

// No-op component kept for backwards compatibility
function JwtUrlHandler() { return null; }

// --- AUTH GUARD ---------------------------------------------------------------

function AuthGuard({ children }) {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setStatus('unauth');
      return;
    }

    authFetch('/auth/me')
      .then(async r => {
        const session = r.ok ? await r.json() : null;
        if (session?.authenticated === true) {
          setStatus('ok');
        } else {
          clearToken();
          setStatus('unauth');
        }
      })
      .catch(() => {
        setStatus('unauth');
      });
  }, []);

  if (status === 'checking') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    );
  }

  if (status === 'unauth' && isAdminView)return <p role="alert">This owner view expired. Use Reconnect owner view in admin.</p>;
  if (status === 'unauth') {
    return <Navigate to={loginDestination(window.location.pathname + window.location.search)} replace />;
  }

  return children;
}

// --- AUTHENTICATED SHELL -----------------------------------------------------

function AppShell() {
  useEffect(connectPreviewLayout, []);
  const { gymName, logoUrl } = useBranding();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState('');
  const signOutPending = useRef(false);

  async function signOut() {
    if (isAdminView || signOutPending.current) return;
    signOutPending.current = true;
    setSigningOut(true);
    setSignOutError('');
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST', credentials: 'include', signal: AbortSignal.timeout(10000),
      });
      if (!response.ok || (await response.json()).logged_out !== true) throw new Error('logout failed');
      clearToken();
      window.location.replace(loginDestination(window.location.pathname + window.location.search) + '&logged_out=1');
    } catch {
      setSignOutError('Could not sign out. Check your connection and try again.');
      signOutPending.current = false;
      setSigningOut(false);
    }
  }

  return (
    <div className="app-shell">
      <header className="top-bar">
        <div className="top-bar-brand">
        {logoUrl
          ? <img src={logoUrl} alt={gymName} className="top-bar-logo" />
          : <span className="top-bar-gym-name">{gymName}</span>
        }
        </div>
        {!isAdminView && <button type="button" className="sign-out-button" onClick={signOut} disabled={signingOut}>{signingOut ? 'Signing out…' : 'Sign out'}</button>}
        {signOutError && <p className="sign-out-error" role="alert">{signOutError}</p>}
      </header>

      <main className="page-content">
        {isAdminView&&<p role="status" className="owner-panel">Admin viewing · read-only. Cards, purchases, submissions and campaign changes are unavailable in this preview.</p>}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/conversations/*" element={<Navigate to="/" replace />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/business" element={<Business />} />
          <Route path="/employee" element={<Business />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/growth" element={<Growth />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <TabBar />

      {/* Install prompt - shows once after login if not in standalone mode */}
      {!isAdminView&&<InstallPrompt />}
    </div>
  );
}

// --- ROOT APP -----------------------------------------------------------------

function AdminRedirect() {
  useEffect(() => { window.location.replace('https://hireaigo.com/admin/'); }, []);
  return <p>Opening <a href="https://hireaigo.com/admin/">HireAiGO Admin</a>...</p>;
}

export default function App() {
  const [ready,setReady]=useState(!isAdminView),[viewError,setViewError]=useState('');
  useEffect(()=>{if(isAdminView)waitForAdminView().then(()=>setReady(true)).catch(e=>setViewError(e.message));},[]);
  if(!ready)return <p role={viewError?'alert':'status'} style={{padding:24}}>{viewError||'Connecting your admin owner view…'}</p>;
  return (
    <>
      {/* Reads ?jwt= from URL if present, stores in localStorage, strips from URL */}
      <JwtUrlHandler />

      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/verify" element={<AuthVerify />} />
        <Route path="/growth-admin" element={<AdminRedirect />} />

        {/* Protected routes */}
        <Route path="/*" element={
          <AuthGuard>
            <GymBrandingProvider>
              <AppShell />
            </GymBrandingProvider>
          </AuthGuard>
        } />
      </Routes>
    </>
  );
}

// Export authFetch for use in page components
export { authFetch, getToken, clearToken };
